import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { crmPostventaV2Api } from "./crmPostventaV2Api";
import { getProcessCodeBySlug, getSectionMeta } from "./processCatalog";
import { getProcessSection } from "./processSections";
import ProcessActionModal, {
  buildAttemptLabel,
  formatDateLabel,
  formatTimeLabel,
  resolvePriorityTone,
} from "./components/ProcessActionModal";
import AlertActionsDropdown from "../crmpostventa/AlertActionsDropdown";
import AlertFilters from "../crmpostventa/AlertFilters";
import AlertScrollArea from "../crmpostventa/AlertScrollArea";
import { dateInputValueFromDisplay } from "../crmpostventa/onboarding-alerts/onboardingAlertsUtils";
import {
  applyAlertFilters,
  buildAlertFilterOptions,
  EMPTY_ALERT_FILTERS,
  hasActiveAlertFilters,
} from "../crmpostventa/alertFiltersUtils";
import "./gestion-unificada.css";

const makeToday = () => new Date().toISOString().slice(0, 10);

const NO_GLOBAL_FILTERS = {};

const PRIORITY_LABELS = {
  low: "Baja",
  medium: "Media",
  high: "Alta",
  critical: "Crítica",
};

function PriorityChip({ value }) {
  if (!value) {
    return <span className="crm-gestion-unificada__prioridad is-neutral">Sin prioridad</span>;
  }
  const tone = resolvePriorityTone(value);
  return (
    <span className={`crm-gestion-unificada__prioridad is-${tone === "low" ? "baja" : tone === "medium" ? "media" : tone === "high" ? "alta" : "critica"}`}>
      {PRIORITY_LABELS[tone] || value}
    </span>
  );
}

function AnticipoChip({ value }) {
  const normalized = String(value || "Inactivo").trim();
  const active = normalized.toLowerCase() !== "activo";

  return (
    <span className={`crm-gestion-unificada__anticipo ${active ? "is-active" : "is-inactive"}`}>
      {active ? "Activo" : "Inactivo"}
    </span>
  );
}

const capitalize = (value) => {
  const normalized = String(value ?? "").trim().toLocaleLowerCase("es-AR");
  return normalized ? normalized.charAt(0).toLocaleUpperCase("es-AR") + normalized.slice(1) : "";
};

function GestionRow({ item, onManage, onTake, onAssign, onRelease, disabled, manageOnly = false }) {
  const nextDate = formatDateLabel(item?.fechaProximaAccion);
  const nextTime = formatTimeLabel(item?.fechaProximaAccion);
  const attemptLabel = buildAttemptLabel(item);
  const nextActionLabel = capitalize(item?.accionPermitida);
  const readOnly = item?.esSoloLectura || item?.puedeGestionar === false;
  const actions = [
    item?.puedeTomar ? { key: "take", label: "Tomar", onSelect: () => onTake?.(item) } : null,
    item?.puedeAsignar ? { key: "assign", label: "Asignar", onSelect: () => onAssign?.(item) } : null,
    item?.puedeLiberar ? { key: "release", label: "Liberar", onSelect: () => onRelease?.(item) } : null,
    { key: "manage", label: readOnly ? "Ver detalle" : "Gestionar", onSelect: () => onManage(item) },
  ].filter(Boolean);

  return (
    <div className="crm-gestion-unificada__row">
      <div className="crm-gestion-unificada__comercio">
        <strong>{item?.comercio || "Sin nombre"}</strong>
        <div className="crm-gestion-unificada__comercio-meta">
          {item?.cuit ? <span><b>CUIT:</b> {item.cuit}</span> : null}
          {item?.numeroTerminal ? <span><b>Terminal:</b> {item.numeroTerminal}</span> : null}
          {item?.numeroComercio ? <span><b>Comercio:</b> {item.numeroComercio}</span> : null}
          {item?.provincia ? <span><b>Provincia:</b> {item.provincia}</span> : null}
        </div>
      </div>

      <div className="crm-gestion-unificada__anticipo-wrap">
        <AnticipoChip value={item?.anticipoActivo || item?.AnticipoActivo} />
      </div>

      <div>
        <span className="crm-gestion-unificada__chip">{item?.estadoLabel || item?.estadoActual || "-"}</span>
        <div className="crm-gestion-unificada__seguimiento-meta">
          {item?.canalPermitido ? <span><b>Canal:</b> {item.canalPermitido}</span> : null}
          <span><b>Intento:</b> {attemptLabel}</span>
        </div>
      </div>

      <div className="crm-gestion-unificada__accion">
        {nextActionLabel ? (
          <div className="crm-gestion-unificada__accion-title">
            <strong>{nextActionLabel}</strong>{item?.canalPermitido ? ` por ${item.canalPermitido}` : ""}
          </div>
        ) : (
          <span>Sin próxima acción</span>
        )}
        {nextDate ? <span><b>Fecha:</b> {nextDate}</span> : null}
        {nextTime ? <span><b>Horario:</b> {nextTime}</span> : null}
      </div>

      <div className="crm-gestion-unificada__prioridad-wrap">
        <PriorityChip value={item?.prioridad} />
      </div>

      <div>
        <span className={`crm-gestion-unificada__responsable ${item?.responsable ? "" : "is-unassigned"}`}>
          {item?.responsable || "Sin asignar"}
        </span>
      </div>

      <div>
        {manageOnly ? (
          <button
            type="button"
            className="crm-gestion-unificada__manage-btn"
            onClick={() => onManage(item)}
            disabled={disabled}
          >
            {readOnly ? "Ver detalle" : "Gestionar"}
          </button>
        ) : (
          <AlertActionsDropdown
            items={actions}
            disabled={disabled}
            ariaLabel={`Acciones para ${item?.comercio || "la gestión"}`}
            toggleClassName="crm-gestion-unificada__actions-button"
          >
            Acciones
          </AlertActionsDropdown>
        )}
      </div>
    </div>
  );
}

export default function GestionProcesoPanel({ slug }) {
  const section = getSectionMeta(slug);
  const sectionEntry = getProcessSection(slug);
  const SectionComponent = sectionEntry?.component || null;
  const processCode = getProcessCodeBySlug(slug);
  const usesSection = Boolean(SectionComponent);
  // Las secciones con feedsRows no fetchean: siguen dependiendo de obtenerCola.
  const sectionFeedsRows = Boolean(sectionEntry?.feedsRows);

  const [fecha, setFecha] = useState(makeToday());
  const [mutationNonce, setMutationNonce] = useState(0);
  const [initialResult, setInitialResult] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_ALERT_FILTERS);
  const [dateFilter, setDateFilter] = useState("");
  const [responsables, setResponsables] = useState([]);

  const normalizedRows = useMemo(
    () => rows.map((item) => ({
      ...item,
      priority: String(item?.prioridad || "").trim(),
      province: String(item?.provincia || "").trim(),
      responsibleName: String(item?.responsable || "").trim(),
      status: String(item?.estadoLabel || item?.estadoActual || "Sin estado").trim(),
    })),
    [rows]
  );

  const filterOptions = useMemo(() => buildAlertFilterOptions(normalizedRows), [normalizedRows]);
  const filteredRows = useMemo(() => {
    const needle = String(search || "").trim().toLowerCase();
    const searchedRows = !needle ? normalizedRows : normalizedRows.filter((item) =>
      [
        item?.comercio,
        item?.cuit,
        item?.numeroTerminal,
        item?.numeroComercio,
        item?.provincia,
        item?.responsable,
        item?.estadoLabel,
        item?.estadoActual,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(needle))
    );
    const alertFilteredRows = applyAlertFilters(searchedRows, filters);
    return dateFilter
      ? alertFilteredRows.filter((item) => dateInputValueFromDisplay(item?.fechaProximaAccion) === dateFilter)
      : alertFilteredRows;
  }, [dateFilter, filters, normalizedRows, search]);
  const hasActiveFilters = hasActiveAlertFilters(search, filters) || Boolean(dateFilter);

  const resetFilters = () => {
    setSearch("");
    setFilters(EMPTY_ALERT_FILTERS);
    setDateFilter("");
  };

  const loadRows = async () => {
    if (usesSection && !sectionFeedsRows) {
      setLoading(false);
      setMutationNonce((prev) => prev + 1);
      return;
    }

    setLoading(true);
    try {
      const response = await crmPostventaV2Api.obtenerCola(slug, { fecha });
      setRows((response?.filas || []).map((item) => ({ ...item, procesoSlug: slug, procesoLabel: section.shortTitle })));
      setResponsables(response?.responsables || []);
    } catch (error) {
      setResponsables([]);
      Swal.fire(section.shortTitle, error.message || "No pudimos cargar las gestiones.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOwnershipAction = async (action, item, body = {}) => {
    try {
      setSubmitting(true);
      const versionToken = body.versionToken || item?.versionToken;
      if (action === "take") {
        await crmPostventaV2Api.tomarProceso(item.id, {
          versionToken,
          motivo: body.motivo || "Toma desde gestión unificada.",
        });
      } else if (action === "assign") {
        await crmPostventaV2Api.asignarProceso(item.id, {
          versionToken,
          usuarioId: body.usuarioId,
          motivo: body.motivo || "Asignación desde gestión unificada.",
        });
      } else if (action === "release") {
        await crmPostventaV2Api.liberarProceso(item.id, {
          versionToken,
          motivo: body.motivo || "Liberación desde gestión unificada.",
        });
      }

      await loadRows();
    } catch (error) {
      Swal.fire(section.shortTitle, error.message || "No pudimos completar la acción.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTake = (item) => handleOwnershipAction("take", item);

  const ensureResponsables = async () => {
    if (!usesSection || sectionFeedsRows || responsables.length) {
      return responsables;
    }

    try {
      const response = await crmPostventaV2Api.obtenerAlertas({
        procesoCodigo: processCode,
        pagina: 1,
        tamanoPagina: 1,
      });
      const loaded = response?.responsables || [];
      setResponsables(loaded);
      return loaded;
    } catch {
      return [];
    }
  };

  const handleAssign = async (item) => {
    const options = {};
    (await ensureResponsables()).forEach((responsable) => {
      options[String(responsable.usuarioId)] = `${responsable.nombre} (${responsable.total})`;
    });

    if (!Object.keys(options).length) {
      Swal.fire(section.shortTitle, "Todavía no hay responsables visibles para asignar desde esta grilla.", "info");
      return;
    }

    const { isConfirmed, value } = await Swal.fire({
      title: "Asignar responsable",
      input: "select",
      inputOptions: options,
      inputPlaceholder: "Seleccionar responsable",
      showCancelButton: true,
      confirmButtonText: "Asignar",
      cancelButtonText: "Cancelar",
      inputValidator: (selected) => (!selected ? "Selecciona un responsable." : undefined),
    });

    if (!isConfirmed || !value) return;

    await handleOwnershipAction("assign", item, { usuarioId: Number(value) });
  };

  const handleRelease = async (item) => {
    const confirm = await Swal.fire({
      title: "Liberar proceso",
      text: "El proceso quedará nuevamente sin responsable.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Liberar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    await handleOwnershipAction("release", item);
  };

  useEffect(() => {
    loadRows();
    resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, fecha]);

  const openDetail = async (item, options = {}) => {
    try {
      setSubmitting(true);
      setInitialResult(options.initialResult || "");
      const processSlug = item.procesoSlug || item.processSlug || slug;
      const [detailResponse, evidencesResponse] = await Promise.all([
        crmPostventaV2Api.obtenerDetalle(processSlug, item.id),
        crmPostventaV2Api.obtenerEvidencias(item.id),
      ]);
      setDetail({
        ...detailResponse,
        instancia: {
          ...detailResponse?.instancia,
          procesoCodigo: detailResponse?.instancia?.procesoCodigo || processSlug,
          procesoSlug: processSlug,
        },
      });
      setExistingEvidences(evidencesResponse || []);
      setModalOpen(true);
    } catch (error) {
      Swal.fire(section.shortTitle, error.message || "No pudimos abrir la gestión.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerar = async () => {
    try {
      setSubmitting(true);
      const action = section.supportsReconcile ? crmPostventaV2Api.reconciliar : crmPostventaV2Api.generar;
      const preview = await action(slug, { dryRun: true, fecha });

      if (!preview?.permiteEjecucionReal) {
        await Swal.fire({
          title: "Ejecución bloqueada",
          text: preview?.message || "No hay elementos para generar en este momento.",
          icon: "warning",
        });
        return;
      }

      const confirmation = await Swal.fire({
        title: "Confirmar generación",
        text: `Se crearán ${preview?.crear ?? 0} procesos, limitados a ${preview?.maximumItemsPerRun ?? 0} por ejecución.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: section.actionLabel || "Generar",
        cancelButtonText: "Cancelar",
      });

      if (!confirmation.isConfirmed) {
        return;
      }

      const response = await action(slug, { dryRun: false, fecha });
      await Swal.fire("Operación completada", response?.message || "Procesos generados correctamente.", "success");
      await loadRows();
    } catch (error) {
      Swal.fire(section.shortTitle, error.message || "No pudimos generar los procesos.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAction = async (payload) => {
    if (!detail?.instancia?.id) return;

    try {
      setSubmitting(true);
      const response = await crmPostventaV2Api.ejecutarAccion(detail.instancia.procesoSlug || slug, detail.instancia.id, payload);
      await Swal.fire(section.shortTitle, response?.message || "Gestión registrada.", "success");
      setModalOpen(false);
      setDetail(null);
      setExistingEvidences([]);
      setInitialResult("");
      await loadRows();
    } catch (error) {
      if (error?.status === 409) {
        const processSlug = detail.instancia.procesoSlug || slug;
        const [detailResponse, evidencesResponse] = await Promise.all([
          crmPostventaV2Api.obtenerDetalle(processSlug, detail.instancia.id),
          crmPostventaV2Api.obtenerEvidencias(detail.instancia.id),
        ]);

        setDetail({
          ...detailResponse,
          instancia: {
            ...detailResponse?.instancia,
            procesoCodigo: detailResponse?.instancia?.procesoCodigo || processSlug,
            procesoSlug: processSlug,
          },
        });
        setExistingEvidences(evidencesResponse || []);
        await loadRows();
        Swal.fire(section.shortTitle, "La gestión fue modificada por otro usuario. Actualizá e intentá de nuevo.", "warning");
      } else {
        Swal.fire(section.shortTitle, error.message || "No pudimos guardar la gestión.", "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canGenerate = false;
  const canRunProcessAction = Boolean(section.supportsGenerate || section.supportsReconcile);
  const processActionLabel = section.supportsReconcile ? "Reconciliar" : (section.actionLabel || "Generar");

  return (
    <div className="crm-gestion-unificada">

      {usesSection ? (
        <>

          {sectionFeedsRows ? (
            <SectionComponent
              {...sectionEntry.props}
              description={section.description}
              isManagementView
              rows={rows}
              loading={loading}
              fecha={fecha}
              onFechaChange={setFecha}
              actionLabel={processActionLabel}
              canRunAction={canRunProcessAction}
              onRunAction={handleGenerar}
              onRefresh={loadRows}
              actionDisabled={loading || submitting}
              onManage={openDetail}
              onTake={handleTake}
              onAssign={handleAssign}
              onRelease={handleRelease}
            />
          ) : (
            <SectionComponent
              {...sectionEntry.props}
              title={section.shortTitle}
              description={sectionEntry.props?.description || section.description}
              isManagementView
              processCode={processCode}
              processSlug={slug}
              globalFilters={NO_GLOBAL_FILTERS}
              onManage={openDetail}
              onTake={handleTake}
              onAssign={handleAssign}
              onRelease={handleRelease}
              onMutationNonce={mutationNonce}
            />
          )}
        </>
      ) : (
        <>
      <div className="crm-gestion-unificada__top-panel">
        <div className="crm-gestion-unificada__top-copy">
          <h3>{section.shortTitle}</h3>
          <p>{section.description}</p>
        </div>

        <div className="crm-gestion-unificada__top-actions">
          {canGenerate ? (
            <button type="button" className="crm-gestion-unificada__generar" onClick={handleGenerar} disabled={loading || submitting}>
              <FontAwesomeIcon icon={faPlus} /> Generar
            </button>
          ) : null}

          <button
            type="button"
            className="crm-gestion-unificada__refresh"
            onClick={loadRows}
            disabled={loading || submitting}
            aria-label="Actualizar"
          >
            <FontAwesomeIcon icon={faRotateRight} spin={loading} />
          </button>
        </div>
      </div>

      <div className="crm-gestion-unificada__filter-row">
        <AlertFilters
          className="crm-gestion-unificada__filters"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Buscar por comercio, CUIT, terminal, provincia o responsable..."
          searchAriaLabel={`Buscar en ${section.shortTitle}`}
          filters={filters}
          onFiltersChange={setFilters}
          options={filterOptions}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
          hiddenKeys={["status"]}
        >
          <Form.Control
            type="date"
            className="crm-alert-filters__date"
            value={dateFilter}
            onChange={(event) => setDateFilter(event.target.value)}
            aria-label="Filtrar por fecha de próxima acción"
          />
        </AlertFilters>
      </div>

      <div className="crm-gestion-unificada__columns" role="row">
        <span>Comercio</span>
        <span>Anticipo</span>
        <span>Seguimiento</span>
        <span>Próx. acción</span>
        <span>Prioridad</span>
        <span>Responsable</span>
        <span>Acciones</span>
      </div>

      {loading ? (
        <div className="crm-gestion-unificada__loading">
          <Spinner animation="border" size="sm" className="me-2" /> Cargando gestiones...
        </div>
      ) : filteredRows.length ? (
        <AlertScrollArea viewportClassName="crm-gestion-unificada__list">
          {filteredRows.map((item) => (
            <GestionRow
              key={`${item.procesoCodigo || slug}-${item.id}`}
              item={item}
              onManage={openDetail}
              onTake={handleTake}
              onAssign={handleAssign}
              onRelease={handleRelease}
              disabled={submitting}
              manageOnly={slug === "onboarding"}
            />
          ))}
        </AlertScrollArea>
      ) : rows.length ? (
        <div className="crm-gestion-unificada__empty">No se encontraron gestiones con los filtros actuales.</div>
      ) : (
        <div className="crm-gestion-unificada__empty">No hay gestiones pendientes para esta categoría.</div>
      )}
        </>
      )}

      <ProcessActionModal
        show={modalOpen}
        onHide={() => {
          setModalOpen(false);
          setInitialResult("");
        }}
        detail={detail}
        existingEvidences={existingEvidences}
        submitting={submitting}
        initialResult={initialResult}
        onSubmit={handleSubmitAction}
      />
    </div>
  );
}
